import emailjs from '@emailjs/browser';

/**
 * Send a single email via EmailJS (Frontend)
 * @param {object} params
 * @returns {Promise<{success:boolean, message:string, simulated?:boolean}>}
 */
export const sendEmail = async (params) => {
  try {
    const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID || 'service_default';
    const templateId = params.templateId || import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'template_default';
    const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || 'public_key_default';

    // Normalize params to ensure EmailJS template compatibility
    const templateParams = {
      to_name: params.toName || params.to_name || '',
      to_email: params.toEmail || params.to_email || '',
      subject: params.subject || '',
      message: params.message || '',
      from_name: params.fromName || params.from_name || 'Sentinel Security',
      first_name: params.firstName || params.first_name || (params.toName ? params.toName.split(' ')[0] : ''),
      organization_name: params.organizationName || params.organization_name || 'Sentinel Security',
      role: params.role || 'Learner',
      expiration_date: params.expirationDate || params.expiration_date || '',
      invite_link: params.inviteLink || params.invite_link || `${window.location.origin}${window.location.pathname}#/login`,
      temp_password: params.tempPassword || params.temp_password || '',
      reply_to: params.replyTo || params.reply_to || 'noreply@sentinelsecurity.com'
    };

    if (serviceId === 'service_default') {
       console.warn('[EmailJS] Credentials missing. Simulating email send.');
       return { success: true, message: 'Simulated email send (Add VITE_EMAILJS credentials to send real emails).', simulated: true };
    }

    const response = await emailjs.send(
      serviceId,
      templateId,
      templateParams,
      publicKey
    );

    return { success: true, message: 'Email sent successfully via EmailJS', response };
  } catch (err) {
    console.error('[EmailService] EmailJS call failed:', err);
    return { success: false, message: err?.message || 'Network error failed to send email.' };
  }
};

/**
 * Send bulk emails sequentially with a small delay to respect rate limits.
 */
export const sendBulkEmails = async (recipients, delayMs = 300) => {
  const results = [];
  let sent = 0;
  let failed = 0;

  for (const recipient of recipients) {
    const result = await sendEmail(recipient);
    results.push({ ...recipient, ...result });
    if (result.success) sent++; else failed++;
    if (delayMs) await new Promise(r => setTimeout(r, delayMs));
  }

  return { sent, failed, results };
};

// ─── Template Renderers ───────────────────────────────────────────────────────
const renderTemplate = (template, vars) =>
  template.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] ?? `{{${key}}}`);

/**
 * Prepares recipient variables for a template engine.
 */
export const buildEmailFromTemplate = (template, learner, extraVars = {}) => {
  const vars = {
    firstName:       learner.firstName,
    learnerName:     `${learner.firstName} ${learner.lastName}`,
    managerName:     learner.manager || 'Your Manager',
    department:      learner.department,
    ...extraVars,
  };

  return {
    toName:  `${learner.firstName} ${learner.lastName}`,
    toEmail: learner.email,
    subject: renderTemplate(template.subject, vars),
    message: renderTemplate(template.body,    vars),
  };
};